"use client";

import * as React from "react";

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import { cn } from "@/lib/utils";
import { ProductDetailsModel } from "@/types/product";
import Image from "next/image";

export default function ProductSlider({
  images,
  productId,
}: {
  images: ProductDetailsModel["images"];
  productId?: number;
}) {
  const [api, setApi] = React.useState<CarouselApi>();
  const [current, setCurrent] = React.useState(0);
  const [count, setCount] = React.useState(0);

  React.useEffect(() => {
    if (!api) {
      return;
    }

    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);

  React.useEffect(() => {
    if (!api || !productId) return;
    const handler = (e: Event) => {
      const color = (e as CustomEvent<string>).detail;
      if (!color) return;
      const idx = images.main.findIndex((img) => img.color === color);
      if (idx >= 0) api.scrollTo(idx);
    };
    const eventName = `product-color-change:${productId}`;
    window.addEventListener(eventName, handler);
    return () => window.removeEventListener(eventName, handler);
  }, [api, images.main, productId]);

  const handleThumbClick = React.useCallback(
    (index: number) => {
      api?.scrollTo(index);
    },
    [api],
  );

  return (
    <div className="space-y-5">
      <Carousel setApi={setApi} className="w-full">
        <CarouselContent>
          {images.main.map((image, index) => (
            <CarouselItem key={index}>
              <div className="relative w-full aspect-[4/3] overflow-hidden rounded-lg">
                <Image
                  src={image.url}
                  alt=""
                  fill
                  className="object-contain"
                  id={`product-img-${productId}`}
                />
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
      <Carousel
        className="w-full max-w-full"
        opts={{
          align: "start",
        }}
      >
        <CarouselContent className="px-2 gap-3">
          {images.main.map((image, index) => (
            <CarouselItem
              key={image.id}
              className={cn(
                "basis-1/4 sm:basis-1/5 cursor-pointer rounded-lg overflow-hidden relative w-full p-0",
                current === index + 1 ? "border-2 border-primary" : "",
              )}
              onClick={() => handleThumbClick(index)}
            >
              <div className="relative w-full h-24">
                <Image
                  src={image.url}
                  alt=""
                  fill
                  className="object-cover rounded-lg"
                />
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
    </div>
  );
}
